// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import Chart from "react-apexcharts"
import type { ApexOptions } from "apexcharts"
import { useParams } from "react-router-dom"
import { useCycleLatestSnapshot } from "@/queries/queries.eventJournal"
import { formatCurrency } from "@/utils"
import { parseCycleSnapshotPayload } from "../_snapshotPayload"
import {
  deriveReferenceCostLine,
  type ReferenceCostLine,
} from "../referenceSet/derive"
import { referenceCostAnnotations } from "../referenceSet/annotations"
import {
  deriveCostByCategory,
  type CostByCategoryRow,
  type CostChartsSnapshotPayload,
} from "./types"
import ChartZeroState from "../ChartZeroState"

// Iso with `ebiose-webapp/src/views/cycles/graph.cost.tsx`.
// Bar chart (ApexCharts, dark theme) — cycle-to-date cost breakdown by category
// (Architect / Genetic / Evaluation), read from the ledger's nested `Spend` fields
// (PRD §M5/§M11). Replaces the pre-split per-iteration decomposition: `CycleSnapshotDto`
// no longer carries a `*_by_iter` cost series (INV-8, breaking — see
// `cycle-ledger-snapshot`), only the cumulative-to-date split per category, so
// "per iteration" became "per category" by necessity, not redesign.
//
// Input/output ventilation: when a category's `Spend.split_available` is `true`
// (see `deriveCostByCategory` in `./types`), the bar gets an Input/Output line overlay.
// Lines, not extra stack segments: input + output re-partitions the same total the bar
// already shows — stacking would double the bar height. Split unavailable → overlay
// point absent (INV-3 — never a fabricated $0).
//
// Accessibility: a visually-hidden `<table>` mirrors per-category values so screen
// readers and tests can read the data without depending on ApexCharts' SVG.
export interface CostChartsProps {
  cycleId?: string
}

export default function CostCharts({ cycleId }: CostChartsProps = {}) {
  const params = useParams<{ cycleId: string }>()
  const resolvedCycleId = cycleId ?? params.cycleId
  const snapshotQuery = useCycleLatestSnapshot(resolvedCycleId)

  // Render nothing while the snapshot read is in flight (no zero-state
  // flash before the source resolves).
  if (snapshotQuery.isLoading) {
    return null
  }

  // Per-panel gating (issue `per-panel-gating-t0`): once the read has
  // resolved without a snapshot row, the panel owns its own zero-state
  // (US §10). A present-but-empty snapshot keeps its prior render-nothing
  // behaviour below (unchanged snapshot-present contract).
  if (!snapshotQuery.isSuccess || snapshotQuery.data == null) {
    return <ChartZeroState message="No cost data yet." />
  }

  const payload = parseCycleSnapshotPayload<CostChartsSnapshotPayload>(
    snapshotQuery.data.payload_json,
  )
  if (payload === null) return null

  const rows = deriveCostByCategory(payload)
  if (rows === null) {
    return null
  }

  const referenceCostLine = deriveReferenceCostLine(payload)

  return (
    <CostDecompositionPanel rows={rows} referenceCostLine={referenceCostLine} />
  )
}

const CostDecompositionPanel = ({
  rows,
  referenceCostLine = null,
}: {
  rows: ReadonlyArray<CostByCategoryRow>
  referenceCostLine?: ReferenceCostLine | null
}) => {
  const categories = rows.map((r) => r.category)
  const hasIoSplit = rows.some((r) => r.prompt != null)

  // Without any split this stays a plain bar chart. With at least one
  // category split, the chart becomes an ApexCharts mixed chart: the
  // totals stay bars, input/output ride as line overlays.
  const barType = hasIoSplit ? ("column" as const) : undefined
  const series: { name: string; type?: "column" | "line"; data: number[] }[] =
    [
      {
        name: "Total",
        type: barType,
        data: rows.map((r) => round2(r.total)),
      },
    ]
  const colors = ["#45f0c8"]
  if (hasIoSplit) {
    series.push(
      {
        name: "Input cost",
        type: "line",
        data: rows.map((r) => round2(r.prompt ?? 0)),
      },
      {
        name: "Output cost",
        type: "line",
        data: rows.map((r) => round2(r.completion ?? 0)),
      },
    )
    colors.push("#68CC58", "#FF8A7E")
  }

  const options: ApexOptions = {
    chart: {
      type: hasIoSplit ? "line" : "bar",
      height: 400,
      stacked: true,
      background: "transparent",
      toolbar: { show: false },
      animations: { enabled: false },
      zoom: { enabled: false },
    },
    theme: { mode: "dark" },
    xaxis: {
      categories,
      labels: { style: { colors: "#5f8c84" } },
      title: {
        text: "Cost category",
        style: { color: "#5f8c84", fontSize: "16px" },
      },
    },
    yaxis: {
      min: 0,
      title: {
        text: "Cost ($)",
        style: { color: "#5f8c84", fontSize: "16px" },
      },
      labels: {
        style: { colors: "#5f8c84" },
        formatter: (value: number) => `$${value.toFixed(2)}`,
      },
    },
    colors,
    // Stroke only matters on the mixed chart: 0-width on the bar
    // series, visible lines on the input/output overlays.
    ...(hasIoSplit
      ? {
          stroke: {
            width: series.map((s) => (s.type === "line" ? 2.5 : 0)),
            curve: "straight" as const,
          },
        }
      : {}),
    plotOptions: { bar: { horizontal: false, columnWidth: "60%" } },
    grid: { borderColor: "#11393f" },
    tooltip: {
      theme: "dark",
      y: { formatter: (value: number) => `$${value.toFixed(2)}` },
    },
    legend: {
      position: "right",
      offsetY: 40,
      labels: { colors: "#a9cfc6" },
    },
    dataLabels: { enabled: false },
    ...(referenceCostLine !== null
      ? { annotations: referenceCostAnnotations(referenceCostLine) }
      : {}),
  }

  return (
    <section
      role="region"
      aria-label="Cost decomposition by category"
      className="flex flex-col gap-2 w-full"
    >
      <div
        data-testid="cost-decomposition-chart"
        {...(referenceCostLine !== null
          ? { "data-reference-cost-line": JSON.stringify(referenceCostLine) }
          : {})}
        role="figure"
        aria-label="Cost decomposition by category chart"
        className="w-full"
      >
        <Chart
          options={options}
          series={series}
          type={hasIoSplit ? "line" : "bar"}
          height={400}
        />
      </div>
      <table className="sr-only">
        <thead>
          <tr>
            <th className="text-left">Category</th>
            <th className="text-left">Total</th>
            {hasIoSplit && <th className="text-left">Input cost</th>}
            {hasIoSplit && <th className="text-left">Output cost</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.category}>
              <td>{r.category}</td>
              <td>{`Total: ${formatCurrency(r.total)}`}</td>
              {hasIoSplit && (
                <td>
                  {r.prompt == null
                    ? "Input: —"
                    : `Input: ${formatCurrency(r.prompt)}`}
                </td>
              )}
              {hasIoSplit && (
                <td>
                  {r.completion == null
                    ? "Output: —"
                    : `Output: ${formatCurrency(r.completion)}`}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {referenceCostLine !== null && (
        <p data-testid="reference-cost-line-caption" className="sr-only">
          {`reference cost/run: ${referenceCostLine.formatted} (best of ${referenceCostLine.n})`}
        </p>
      )}
    </section>
  )
}

const round2 = (n: number): number => Math.round(n * 100) / 100
